import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  CalendarCheck,
  ChevronDown,
  History,
  LogOut,
  BookOpen,
  MonitorSmartphone,
  ScrollText,
  Tag,
  ShieldCheck,
  ShieldUser,
  Settings2,
  Table2,
  Wallet,
  UserCog,
  UserRound,
  Users,
} from 'lucide-react';
import { clsx } from 'clsx';

import { useAuth } from './contexto/auth.tsx';
import { Carregando } from './componentes/ui.tsx';
import { Login } from './paginas/Login.tsx';
import { Fechamento } from './paginas/Fechamento.tsx';
import { Funcionarios } from './paginas/Funcionarios.tsx';
import { BaseAgendamentos } from './paginas/BaseAgendamentos.tsx';
import { Historico } from './paginas/Historico.tsx';
import { Salarios } from './paginas/Salarios.tsx';
import { FechamentoDetalhe } from './paginas/FechamentoDetalhe.tsx';
import { Regras } from './paginas/Regras.tsx';
import { Usuarios } from './paginas/Usuarios.tsx';
import { Perfil } from './paginas/Perfil.tsx';
import { Auditoria } from './paginas/Auditoria.tsx';
import { Permissoes } from './paginas/Permissoes.tsx';
import { Ajuda } from './paginas/Ajuda.tsx';
import { Sessoes } from './paginas/Sessoes.tsx';
import { Novidades } from './paginas/Novidades.tsx';

interface Link {
  para: string;
  rotulo: string;
  Icone: typeof CalendarCheck;
  /**
   * Módulo que o link precisa ver. Sem isto, aparece para qualquer um que
   * esteja logado. O menu deixou de citar perfis: agora pergunta permissão,
   * então um perfil novo criado na tela já aparece com os itens certos.
   */
  modulo?: string;
  /** Perfis e permissões, que só o administrador administra. */
  somenteAdmin?: boolean;
}

/**
 * Um grupo vira um item só, que abre a lista.
 *
 * As telas de administração são as menos usadas no dia a dia e eram as que
 * empurravam o menu para além da largura da tela. Agrupadas, o menu volta a
 * caber sem barra de rolagem, e quem usa o sistema para fechar o mês não
 * precisa passar os olhos por elas toda vez.
 */
interface Grupo {
  rotulo: string;
  Icone: typeof CalendarCheck;
  itens: Link[];
}

type ItemDeMenu = Link | Grupo;

const ehGrupo = (i: ItemDeMenu): i is Grupo => 'itens' in i;

const MENU: ItemDeMenu[] = [
  { para: '/fechamento', rotulo: 'Fechamento', Icone: CalendarCheck, modulo: 'fechamento' },
  { para: '/historico', rotulo: 'Histórico', Icone: History, modulo: 'fechamento' },
  { para: '/funcionarios', rotulo: 'Funcionários', Icone: Users, modulo: 'funcionarios' },
  { para: '/agendamentos', rotulo: 'Agendamentos', Icone: Table2, modulo: 'fechamento' },
  { para: '/regras', rotulo: 'Regras', Icone: Settings2, modulo: 'regras' },
  { para: '/salarios', rotulo: 'Salários', Icone: Wallet, modulo: 'salarios' },
  {
    rotulo: 'Administração',
    Icone: ShieldUser,
    itens: [
      { para: '/usuarios', rotulo: 'Usuários', Icone: UserCog, modulo: 'usuarios' },
      { para: '/permissoes', rotulo: 'Permissões', Icone: ShieldCheck, somenteAdmin: true },
      { para: '/sessoes', rotulo: 'Sessões', Icone: MonitorSmartphone, modulo: 'sessoes' },
      { para: '/novidades', rotulo: 'Novidades', Icone: Tag, modulo: 'changelog' },
    ],
  },
  { para: '/auditoria', rotulo: 'Log', Icone: ScrollText, modulo: 'log' },
  { para: '/ajuda', rotulo: 'Como funciona', Icone: BookOpen },
];

const CLASSE_DO_ITEM =
  'inline-flex shrink-0 items-center gap-2 rounded px-3 py-1.5 text-sm whitespace-nowrap transition';

/**
 * Item do menu que abre uma lista.
 *
 * Fecha ao clicar fora, ao apertar Esc e ao navegar — um menu que fica aberto
 * depois da escolha tapa justamente o conteúdo que a pessoa foi ver.
 */
function GrupoDoMenu({ grupo, itens }: { grupo: Grupo; itens: Link[] }) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const local = useLocation();

  const algumAtivo = itens.some((i) => local.pathname.startsWith(i.para));

  useEffect(() => setAberto(false), [local.pathname]);

  useEffect(() => {
    if (!aberto) return;

    const foraDaCaixa = (e: MouseEvent) => {
      if (caixa.current !== null && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };

    document.addEventListener('mousedown', foraDaCaixa);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa);
      document.removeEventListener('keydown', escape);
    };
  }, [aberto]);

  return (
    <div className="relative" ref={caixa}>
      <button
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className={clsx(
          CLASSE_DO_ITEM,
          // Destacado quando a tela aberta é uma das de dentro: senão o menu
          // não mostraria onde a pessoa está.
          algumAtivo
            ? 'bg-slate-900 text-white'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )}
      >
        <grupo.Icone className="size-4" />
        {grupo.rotulo}
        <ChevronDown className={clsx('size-3.5 transition', aberto && 'rotate-180')} />
      </button>

      {aberto && (
        <div
          role="menu"
          className="absolute left-0 z-20 mt-1 min-w-52 rounded border border-slate-200 bg-white py-1 shadow-lg"
        >
          {itens.map(({ para, rotulo, Icone }) => (
            <NavLink
              key={para}
              to={para}
              role="menuitem"
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap',
                  isActive
                    ? 'bg-slate-100 font-medium text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                )
              }
            >
              <Icone className="size-4 text-slate-400" />
              {rotulo}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function App() {
  const { usuario, carregando, sair, pode, ehAdmin } = useAuth();

  if (carregando) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Carregando texto="Verificando sua sessão…" />
      </div>
    );
  }

  if (usuario === null) return <Login />;

  const podeVer = (l: Link): boolean =>
    l.somenteAdmin === true ? ehAdmin : l.modulo === undefined || pode(l.modulo, 'visualizar');

  // Um grupo cujos itens a pessoa não pode ver não aparece — e um grupo com um
  // item só continua sendo grupo, para o menu não mudar de forma conforme o
  // perfil de quem olha.
  const visiveis = MENU.map((item) =>
    ehGrupo(item) ? { ...item, itens: item.itens.filter(podeVer) } : item,
  ).filter((item) => (ehGrupo(item) ? item.itens.length > 0 : podeVer(item)));

  // A primeira tela é a primeira que a pessoa tem permissão de ver. Mandar
  // todo mundo para o fechamento deixaria um perfil restrito preso numa tela
  // de acesso negado, sem entender por quê.
  const primeiro = visiveis[0];
  const inicio =
    primeiro === undefined
      ? '/perfil'
      : ehGrupo(primeiro)
        ? (primeiro.itens[0]?.para ?? '/perfil')
        : primeiro.para;

  return (
    <div className="min-h-screen">
      {/* Duas faixas: identidade e usuário em cima, navegação embaixo. Numa faixa
          só, o bloco do usuário quebrava para a linha de baixo assim que o menu
          cresceu, deixando um vão no meio do cabeçalho. Com faixas separadas o
          layout não depende de quantos itens o menu tem. */}
      <header className="relative z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 pt-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Premiação · Call Center</p>
            <p className="text-xs text-slate-500">Skinlaser</p>
          </div>

          <div className="flex items-center gap-1">
            <NavLink
              to="/perfil"
              className="flex items-center gap-2 rounded px-2 py-1 text-right hover:bg-slate-100"
              title="Meu perfil"
            >
              <div>
                <p className="text-sm text-slate-700">{usuario.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{usuario.perfil}</p>
              </div>
              <UserRound className="size-4 text-slate-400" />
            </NavLink>
            <button
              onClick={sair}
              title="Sair"
              className="rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>

        {/* Sem `overflow` nenhum, de propósito. Bastava `overflow-x-auto` para
            o eixo vertical deixar de ser visível — é como o CSS funciona: com
            um eixo recortado, o outro também é — e a lista suspensa dos grupos
            sairia cortada logo abaixo do botão. Em tela estreita o menu quebra
            para a linha de baixo, que era o objetivo do agrupamento: caber sem
            barra de rolagem. */}
        <div className="mx-auto max-w-7xl px-6 pb-2">
          <nav className="flex flex-wrap gap-1">
            {visiveis.map((item) =>
              ehGrupo(item) ? (
                <GrupoDoMenu key={item.rotulo} grupo={item} itens={item.itens} />
              ) : (
                <NavLink
                  key={item.para}
                  to={item.para}
                  className={({ isActive }) =>
                    clsx(
                      CLASSE_DO_ITEM,
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                    )
                  }
                >
                  <item.Icone className="size-4" />
                  {item.rotulo}
                </NavLink>
              ),
            )}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <Routes>
          <Route path="/fechamento" element={<Fechamento />} />
          <Route path="/funcionarios" element={<Funcionarios />} />
          <Route path="/agendamentos" element={<BaseAgendamentos />} />
          <Route path="/historico" element={<Historico />} />
          <Route path="/salarios" element={<Salarios />} />
          <Route path="/historico/:id" element={<FechamentoDetalhe />} />
          <Route path="/regras" element={<Regras />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="/perfil" element={<Perfil />} />
          <Route path="/permissoes" element={<Permissoes />} />
          <Route path="/ajuda" element={<Ajuda />} />
          <Route path="/sessoes" element={<Sessoes />} />
          <Route path="/novidades" element={<Novidades />} />
          <Route path="/auditoria" element={<Auditoria />} />
          <Route path="*" element={<Navigate to={inicio} replace />} />
        </Routes>
      </main>
    </div>
  );
}
